import { AjudinhaProps, ApiConfig, Campo, ModalAtualizacaoEntidade } from "../../services/tipos";
import { TipoFormulario } from "../../services/constantes";

export interface Page {
  titulo: string;
  campos: string[];
}

/** Texto de um balão do guia. */
export interface DicaGuia {
  titulo: string;
  texto: string;
}

/** Aviso do guia que o usuário confirma ("Entendi") ou que some quando a condição deixa de valer. */
export interface AvisoGuia extends DicaGuia {
  /** Nome do campo apontado (o mesmo de `campos`). */
  alvo: string;
  /** Rótulo do botão que dispensa o aviso. Padrão: "Entendi". */
  botao?: string;
}

/**
 * Criação guiada: um balão aponta, na página atual, o que falta fazer. Campos obrigatórios vazios
 * e avisos ainda não dispensados entram na mesma fila, na ordem em que aparecem na tela; com a
 * página completa, o balão vai para o Avançar/Salvar. Segue o estado do formulário, então nunca
 * pula um campo.
 */
export interface GuiaFormulario {
  /** Balão de cada campo obrigatório. Sem dica, usa o rótulo do campo. */
  dicas?: Record<string, DicaGuia>;
  avisos?: (tituloPagina: string, entidade: any) => AvisoGuia[];
  /** Balão do botão Avançar (ou Salvar, na última página). */
  fimDaPagina: (tituloPagina: string, entidade: any, ultimaPagina: boolean) => DicaGuia;
  /** Chamado uma vez por balão novo que aparece (chave "tipo|página|campo"; ordem a partir de 1). */
  aoExibirPasso?: (passo: { chave: string; titulo: string; ordem: number }) => void;
}

export interface BotaoExtraModal {
  onClick: (entidade: any) => void;
  titulo: string;
  cor: string;
}

export interface PropsModalForm {
  show: boolean;
  campos: Campo[];
  largura?: string;
  onHide: () => void;
  onShow?: () => void;
  carregando: boolean;
  onSave?: (entidade: any) => void;
  onCancel: () => void;
  modo: TipoFormulario;
  apiConfig: ApiConfig;
  ajudinha?: AjudinhaProps;
  entidadeSelecionada?: any;
  tamanho: "sm" | "lg" | "xl";
  /**
   * Formulário longo, como as permissões do usuário: o corpo ocupa toda a altura que sobra no modal (o rodapé
   * continua sempre visível) e o indicador de etapas fica numa linha só.
   */
  corpoAlto?: boolean;
  titulo?: string;
  tituloBotaoOk?: string;
  corBotaoOk?: string;
  isSaving?: boolean;
  atualizacaoEntidade?: ModalAtualizacaoEntidade;
  footerText?: Array<{
    text: string;
    color?: string;
    bold?: boolean;
  }>;
  /**
   * Conteúdo livre no rodapé, à esquerda dos botões, no mesmo lugar do footerText (os dois podem
   * coexistir; este vem primeiro). Existe para controles que valem para o formulário inteiro e
   * precisam ficar visíveis em todas as páginas do modal, como o liga/desliga de envio ao Asaas
   * na locação. Não participa de `campos`: quem usa cuida do estado e de colocar o valor no payload.
   */
  footerContent?: React.ReactNode;
  url?: string;
  pages?: Page[];
  pageIndex?: number;
  currentPageIndex?: (index: number) => void;
  planoPremium?: boolean;
  // Mantém o botão OK clicável no modo visualização (ex: fluxo de conferência/aprovação,
  // onde o formulário é só-leitura mas o OK executa uma ação como "Aprovar").
  habilitarBotaoOkEmVisualizacao?: boolean;
  onImageRemoved?: (campo: string) => void;
  onFileRemoved?: (campo: string) => void;
  onVideoRemoved?: (campo: string) => void;
  botaoExtra?: BotaoExtraModal;
  botoesExtras?: BotaoExtraModal[];
  urlTutorial?: string;
  /** Liga a criação guiada (ver GuiaFormulario). */
  guia?: GuiaFormulario;
  /**
   * Mesmo formulário, mas acoplado à página (cartão no fluxo da tela) em vez de janela flutuante:
   * sem fundo escuro, sem rolagem interna e sem fechar/cancelar. Usado no onboarding.
   */
  embutido?: boolean;
}

export interface FieldRendererProps {
  campo: Campo;
  modo: TipoFormulario;
  entidade: any;
  getFieldValue: (campo: string) => any;
  handleInputChange: (campo: string, valor: string | number | boolean) => void;
  handleBlur: (campo: Campo, value?: any) => void;
  errors: Record<string, string>;
  planoPremium?: boolean;
  imagePreview?: Record<string, string>;
  filePreview?: Record<string, { name: string; size: string; url: string }>;
  videoPreview?: Record<string, string>;
  dragActive?: Record<string, boolean>;
  handleImageChange?: (campo: string, file: File) => void;
  handleFileChange?: (campo: string, file: File) => void;
  handleVideoChange?: (campo: string, file: File) => void;
  handleImageRemove?: (campo: string) => void;
  handleFileRemove?: (campo: string) => void;
  handleVideoRemove?: (campo: string) => void;
  handleDrag?: (e: React.DragEvent, campo: string, active: boolean) => void;
  handleDrop?: (e: React.DragEvent, campo: string, tipo: 'image' | 'file' | 'video') => void;
  handleDownloadFile?: (url: string, filename: string) => void;
  handleViewImage?: (campo: string) => void;
  showImageModal?: boolean;
  videoRecording?: VideoRecordingState;
  currentVideoField?: string;
  startVideoRecording?: (campo: string) => void;
  stopVideoRecording?: () => void;
  cancelVideoRecording?: () => void;
}

export interface VideoRecordingState {
  isRecording: boolean;
  mediaRecorder: MediaRecorder | null;
  stream: MediaStream | null;
  recordedChunks: Blob[];
}

export const EXTENSOES_IMAGEM_PERMITIDAS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
export const EXTENSOES_ARQUIVO_PERMITIDAS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'heic', 'heif'];
export const EXTENSOES_VIDEO_PERMITIDAS = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'];
export const TAMANHO_MAXIMO_IMAGEM = 10 * 1024 * 1024;
export const TAMANHO_MAXIMO_ARQUIVO = 20 * 1024 * 1024;
export const TAMANHO_MAXIMO_VIDEO = 100 * 1024 * 1024;
